import {
  and,
  collection,
  doc,
  getDocs,
  onSnapshot,
  or,
  orderBy,
  query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  DocumentData,
  startAfter,
  where,
  updateDoc,
} from 'firebase/firestore';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '@hooks/useAuthContext';
import { firestore } from '@/utils';
import { Notification } from '../entities/notification';
import { Counts } from '../entities/count';

const processNotifications = (notificationsData: DocumentData[]): Notification[] =>
  notificationsData.map((notification) => {
    const { id, topic, type, read, metadata, event_name, created_at } = notification || {};

    return new Notification({
      id,
      topic,
      type,
      read,
      metadata,
      timestamp: created_at,
      eventName: event_name,
    });
  });

export const usePrintQuery = (topic: string) => {
  const [prints, setPrints] = useState<Notification[]>([]);
  const [totalPrints, setTotalPrints] = useState<number>(0);
  const [unreadPrints, setUnreadPrints] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const unsubscribeRef = useRef<() => void>(() => {});
  const { user } = useAuthContext();
  const branchSelected = useMemo(() => user?.branchSelected?.id, [user]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!firestore) {
        throw new Error('Firestore is not initialized');
      }
      const base = collection(firestore, 'branchs');
      const usersRef = doc(base, `${branchSelected}`);
      const notificationsRef = collection(usersRef, 'prints');
      const notificationItem = doc(notificationsRef, id);
      const response = await updateDoc(notificationItem, {
        print: true,
      });
      return response;
    },
    [branchSelected]
  );

  const createPrintQuery = (
    id: string,
    lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>
  ) => {
    if (!firestore || !branchSelected) {
      throw new Error('Firestore is not initialized');
    }
    const branchRef = doc(collection(firestore, 'branchs'), id);
    const printRef = collection(branchRef, 'prints');

    const filterConstraints = [
      where('print', '==', false),
      where('topic', '==', topic),
      or(where('channel', '==', 'DITTO'), where('channel', '==', 'ALL')),
      ...(user?.companySelected?.id ? [where('company_id', '==', user.companySelected.id)] : []),
      where('branch_id', '==', branchSelected),
    ];
    const baseQuery = query(printRef, and(...filterConstraints));
    const constraints = [
      orderBy('created_at', 'desc'),
      ...(lastVisible ? [startAfter(lastVisible)] : []),
    ];

    return query(baseQuery, ...constraints);
  };

  const mapSnapshotToNotifications = (
    snapshot: QuerySnapshot<DocumentData, DocumentData>
  ): DocumentData[] =>
    snapshot?.docs?.map((docTemp: QueryDocumentSnapshot<DocumentData, DocumentData>) => ({
      ...docTemp.data(),
      id: docTemp.id,
    })) ?? [];

  const handleCountsUpdate = (counts: Counts) => {
    setTotalPrints(counts.getTotal());
    setUnreadPrints(counts.getUnread());
  };

  const handleNotificationsUpdate = (notificationsData: DocumentData[]) => {
    const newNotifications = processNotifications(notificationsData);
    setPrints(newNotifications);
  };

  const fetchPrints = useCallback(
    async (id: string, pageSize?: number) => {
      setIsLoading(true);
      try {
        const notificationsQuery = createPrintQuery(id);
        const snapshot = await getDocs(notificationsQuery);
        const notificationsTemp = mapSnapshotToNotifications(snapshot);
        const userNotifications = processNotifications(notificationsTemp);
        setPrints(userNotifications);
      } catch (error) {
        console.error('Error fetching notifications: ', error);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branchSelected, user?.id]
  );

  const onRealtimeNotificationsUpdates = (id: string): (() => void) => {
    try {
      const notificationsQuery = createPrintQuery(id);

      return onSnapshot(
        notificationsQuery,
        (snapshot) => {
          try {
            const notificationsTemp = mapSnapshotToNotifications(snapshot);
            handleNotificationsUpdate(notificationsTemp);
          } catch (snapshotError) {
            console.error('Error processing Firestore snapshot: ', snapshotError);
          }
        },
        (error) => {
          console.error('Error with Firestore snapshot listener: ', error);
        }
      );
    } catch (error) {
      console.error('Error setting up Firestore snapshot listener: ', error);
      return () => {};
    }
  };

  const startRealtimeUpdates = useCallback(
    (id: string) => {
      try {
        if (firestore) {
          const notificationUnsubscribe = onRealtimeNotificationsUpdates(`${id}`);
          const combinedUnsubscribe = () => {
            notificationUnsubscribe();
          };
          unsubscribeRef.current = combinedUnsubscribe;
        }
      } catch (error) {
        console.error('Error starting realtime updates: ', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branchSelected, user?.id]
  );

  const stopRealtimeUpdates = useCallback(() => {
    unsubscribeRef.current();
  }, []);

  return {
    markAsRead,
    prints,
    totalPrints,
    unreadPrints,
    fetchPrints,
    startRealtimeUpdates,
    stopRealtimeUpdates,
    isLoading,
  };
};
